import type { ReactNode } from "react";
import { AlertTriangle, Clock, Loader2, WifiOff } from "lucide-react";

/** One compact, centered status for every moment of a Discover app surface:
 *  launching, waiting out a 429, connection loss, launch failure, the end of a
 *  Try (stopped or expired). The app window header already names the app, so
 *  this stays a status (icon + one line + one short explanation + actions)
 *  instead of a second page about the same app.
 *
 *  The wrapper is deliberately layout-neutral: the caller places it over the
 *  surface it describes (absolute inset in the window body) or inside an empty
 *  stage. It only owns the card's internal rhythm and the CTA sizing. */
export type SurfaceStateIcon = "spinner" | "clock" | "alert" | "offline";

export type SurfaceStateAction = {
  label: ReactNode;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  /** Spinner in place of the label, for an action already in flight. */
  busy?: boolean;
  kind?: "primary" | "secondary";
};

export default function SurfaceState({
  icon = "spinner",
  title,
  description,
  actions = [],
  note,
  className = "",
  actionsClassName = "",
  role = "status",
  live = "polite",
  busy = false,
}: {
  icon?: SurfaceStateIcon;
  title: ReactNode;
  description?: ReactNode;
  actions?: SurfaceStateAction[];
  note?: ReactNode;
  className?: string;
  actionsClassName?: string;
  role?: "status" | "alert";
  live?: "polite" | "assertive" | "off";
  busy?: boolean;
}) {
  return (
    <div
      className={`surface-state ${className}`.trim()}
      role={role}
      aria-live={live}
      aria-busy={busy || undefined}
    >
      <div className="surface-state-body">
        <span className="surface-state-icon" aria-hidden="true">
          {icon === "spinner" ? (
            <Loader2 size={22} className="surface-state-spinner" />
          ) : icon === "clock" ? (
            <Clock size={22} />
          ) : icon === "alert" ? (
            <AlertTriangle size={22} />
          ) : (
            <WifiOff size={22} />
          )}
        </span>
        <h2 className="surface-state-title">{title}</h2>
        {description ? <p className="surface-state-description">{description}</p> : null}
        {actions.length ? (
          <div className={`surface-state-actions ${actionsClassName}`.trim()}>
            {actions.map((action, index) =>
              action.href ? (
                <a
                  key={index}
                  className={`btn ${action.kind === "primary" ? "btn--primary" : ""}`.trim()}
                  href={action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {action.label}
                </a>
              ) : (
                <button
                  key={index}
                  type="button"
                  className={`btn ${action.kind === "primary" ? "btn--primary" : ""}`.trim()}
                  disabled={action.disabled}
                  onClick={action.onClick}
                >
                  {action.busy ? <span className="surface-state-action-spinner" aria-hidden="true" /> : null}
                  {action.label}
                </button>
              ),
            )}
          </div>
        ) : null}
        {note ? <p className="surface-state-note">{note}</p> : null}
      </div>
    </div>
  );
}
